/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import {
    object, shape, string, func,
    number,
} from 'prop-types';
import { uid } from 'react-uid';
import ReactHtmlParser from 'react-html-parser';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';

import { getPmallSummaryInfo } from '../../../../state/ducks/Common/Common-Selectors';
import { getCartAttributeByNameAndReturnValue, getPersAttrValueByName } from '../../page/pageHelpers';
import { CrossMerch } from '../../../components/GraphqlComponents/CrossMerchContainer/CrossMerchContainer';
import { getFeatureFlag, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import KlarnaCreditPromotionBadge from '../../../components/Checkout/KlarnaPaymentOnSiteMessage/KlarnaCreditPromotionBadge/KlarnaCreditPromotionBadge';
import AddonVerification from '../../../components/GraphqlComponents/GraphqlProductPage/Partials/CommonProductAgeZipVerify/CommonProductAgeZipVerify';
import PDPCardIsleGiftCard from '../CardIsleFeature/PDPCardIsleGiftCard';

const styles = (theme) => ({
    paperRoot: {
        marginBottom: '20px',
        width: '100%',
        color: '#000',
        [theme.breakpoints.down('sm')]: {
            marginBottom: '10px',
        },
    },
    marginBottom: {
        marginBottom: '20px',
    },
    personalizationButtonBackgroundFood: {
        background: theme.palette.primaryButton || theme.palette.cms?.flowerPdpButton || theme.palette.secondary?.main,
        '&:hover, &:focus, &:active': {
            background: theme.palette.primaryButton || theme.palette.cms?.flowerPdpButton || theme.palette.secondary?.main,
        },
    },
    personalizationButtonBackgroundFlowers: {
        background: theme.palette.cms?.flowerPdpButton || theme.palette.secondary?.main,
        '&:hover, &:focus, &:active': {
            background: theme.palette.cms?.flowerPdpButton || theme.palette.secondary?.main,
        },
    },
    personalizationButton: {
        marginRight: '10px',
        width: '100%',
        height: '52px',
        fontSize: '16px',
        textAlign: 'center',
        lineHeight: '52px',
        color: 'white',
        border: 'none',
        outline: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        textTransform: 'none',
    },
    paperHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        width: '100%',
        height: '40px',
        padding: '12px 20px',
        fontWeight: '600',
        fontSize: '13px',
        lineHeight: '19px',
        background: theme.palette.grey9,
        borderRadius: '4px 4px 0 0',
    },
    paperBody: {
        boxSizing: 'border-box',
        padding: '13px 20px 20px 12px',
        [theme.breakpoints.down('sm')]: {
            padding: '12px 20px 20px 12px',
        },
    },
    pmallDetails: {
        margin: '10px 0 0',
        fontSize: '16px',
    },
    reviewText: {
        display: 'block',
        width: '100%',
        fontSize: '15px',
        lineHeight: '22px',
        fontWeight: '400',
    },
    btnCta: {
        color: theme.palette.colorInfo,
        padding: '0',
        margin: '0 5px',
        textTransform: 'none',
        fontSize: '15px',
        lineHeight: '22px',
        fontWeight: '500',
        minWidth: '35px',
    },
    '&:hover, &:focus, &:active': {
        color: theme.palette.colorInfo,
        textDecoration: 'underline',
    },
    pdpSummary: {
        padding: '10px 0',
    },
    pdpImg: {
        display: 'block',
        width: '90%',
        maxWidth: '140px',
        margin: '0 0 10px',
    },
    descriptionBox: {
        position: 'relative',
        fontSize: '14px',
        textAlign: 'left',
    },
    closeToggle: {
        maxHeight: '0',
        overflow: 'hidden',
    },
    contBtn: {
        display: 'flex',
        alignItems: 'right',
        justifyContent: 'right',
    },
    pmallDetailReview: {
        margin: '10px',
        fontSize: '14px',
    },
    pmallDetailConfirmation: {
        margin: '10px 15px',
        fontSize: '14px',
    },
});

const PmallSummary = ({
    classes,
    product,
    itemDetails,
    pageType,
    pmallSummaryInfo,
    onHandleModalPmall,
    currentBrandPresentation,
    selectedSku,
    location,
    quantity,
    handleMiniCartModalClick,
}) => {
    const { featureFlags: flags = {}, presentation_family = '' } = useContext(CrossMerch);
    const [openModal, setOpenModal] = useState(false);
    // Adding this feature for brand specific for eg. enabling new pmall ATC for HD only
    const ffCurrentBrandPmallAtcOnSave = useSelector(getFeatureFlag('is-pmall-product-save-atc-enabled'));
    const isPmallEnabled = flags['is-pmall-enabled'];
    const isEditPmallEnabled = flags['is-pmall-edit-enabled'];

    const isPmallPersonalizationAttr = (selectedSku?.attributes || [])?.find((attr) => attr.name === 'is_pmall_personalization_enabled');
    const isPmallPersonalizationEnabled = isPmallPersonalizationAttr?.values?.[0]?.value === 'true';
    const isPmallPersonalizationTypeAttr = (selectedSku?.attributes || [])?.find((attr) => attr.name === 'personalization_type_enabled');
    const pmallPersonalizationType = !!isPmallPersonalizationTypeAttr?.values?.[0]?.value;
    const cartIconLabel = flags['cart-icon-label']?.label;
    // is_pmall_personalization_enabled
    if (!isPmallEnabled || (pageType === 'pdp' && product?.brandId !== '1030' && product?.brandId !== '1033' && !isPmallPersonalizationEnabled &&  !pmallPersonalizationType) || ((product?.brandId === '1030' || product?.brandId === '1033') && !selectedSku?.personalization?.personalizationAllowed)) {
        return null;
    }

    const [openToggle, setOpenToggle] = useState(false);

    // page type pdp or checkout
    if (
        pageType !== 'pdp'
        && (
            !isPmallEnabled
            || !itemDetails?.product?.productCode
            || !itemDetails?.personalization
            || !itemDetails.personalization.personalizationId
            || !itemDetails.personalization.personalizationLines
            || (
                itemDetails.personalization.personalizationType
                && itemDetails.personalization.personalizationType !== 'EXTERNAL'
            )
        )
    ) { return null; }

    const refnum = pmallSummaryInfo?.customizations?.refnum || null;
    let description = pmallSummaryInfo?.customizations?.description || '';
    if (pageType !== 'pdp') {
        description = (
            <div className={openToggle ? `${classes.descriptionBox}` : `${classes.descriptionBox} ${classes.closeToggle}`}>
                {
                    itemDetails?.personalization?.personalizationLines
                        ? ReactHtmlParser(getPersAttrValueByName('description', itemDetails.personalization.personalizationLines))
                        : null
                }
            </div>
        );
    }

    let personalizationButtonText = ffCurrentBrandPmallAtcOnSave ? `Personalize & Add To ${cartIconLabel}` : 'Personalize';
    if (refnum && refnum !== '') {
        personalizationButtonText = 'Edit Personalization';
    }

    let classDetails = classes.pmallDetails;
    if (['payment-review', 'review'].includes(pageType) && presentation_family !== 'food') {
        classDetails = `${classes.pmallDetails} ${classes.pmallDetailReview}`;
    } else if (pageType === 'order-confirmation' && presentation_family !== 'food') {
        classDetails = `${classes.pmallDetails} ${classes.pmallDetailConfirmation}`;
    }
    const handleAddToCart = () => {
        onHandleModalPmall(true, refnum);
    };
    const pmallButton = (styleClass = '') => (
        <Button
            className={`${classes.personalizationButton} ${styleClass} ${currentBrandPresentation === 'food' ? classes.personalizationButtonBackgroundFood : classes.personalizationButtonBackgroundFlowers}`}
            onClick={() => {
                if (selectedSku?.ageVerifyFlag === 'true') {
                    // If ageVerifyFlag is true, open the wine modal
                    setOpenModal(true);
                } else {
                    // If ageVerifyFlag is false, open the personalization Modal
                    onHandleModalPmall(true, refnum);
                }
            }}
        >
            {personalizationButtonText}
        </Button>
    );

    const pmallButtonContainer = (
        <Paper className={classes.paperRoot}>
            <div className={classes.paperHeader}>
                1. Enter Personalization
            </div>
            <div className={classes.paperBody}>
                {
                    pmallSummaryInfo.customizations
                    && (
                        <Grid container>
                            <Grid item xs={3} md={3} lg={3}>
                                <img className={classes.pdpImg} src={`https:${pmallSummaryInfo.customizations.imageURL_thumb}&cacheBust=${pmallSummaryInfo?.countCacheBust}`} alt="" />
                            </Grid>
                            <Grid className={classes.pdpSummary} item xs={9} md={9} lg={9}>
                                {ReactHtmlParser(description)}
                            </Grid>
                        </Grid>
                    )
                }
                {pmallButton()}
            </div>
        </Paper>
    );

    const renderButton = ffCurrentBrandPmallAtcOnSave
        ? (
            <>
                { pmallButton(classes.marginBottom)}
                <KlarnaCreditPromotionBadge
                    selectedSku={selectedSku}
                />
            </>
        ) : pmallButtonContainer;
    return (
        <>
            {pageType === 'pdp'
                ?  (
                    <>
                        {isPmallPersonalizationTypeAttr?.values?.[0]?.value === 'card_isle' ? (
                            <PDPCardIsleGiftCard
                                classes={classes}
                                currentBrandPresentation={currentBrandPresentation}
                                selectedSku={selectedSku}
                                pageType="pdp"
                                handleMiniCartModalClick={handleMiniCartModalClick}
                                featureFlags={flags}
                            />
                        ) : renderButton}
                    </>
                )
                : (
                    <Grid key={uid('pmallsummary', itemDetails?.cartItemId)} container className={classDetails}>
                        <b>Personalization: </b>
                        <Button
                            type="button"
                            className={classes.btnCta}
                            onClick={() => setOpenToggle(!openToggle)}
                        >
                            {!openToggle ? ' View' : ' Hide'}
                        </Button>
                        {!['cart', 'order-confirmation'].includes(pageType) && isEditPmallEnabled && (
                            <> /
                                <Button
                                    id="btn-edit-pmall-personalization"
                                    name="recipientEditPersonalizationButton"
                                    className={classes.btnCta}
                                    data-test="btn-edit-pmall-personalization"
                                    aria-label="Personalization Pmall Edit"
                                    onClick={() => onHandleModalPmall(true, itemDetails?.personalization?.personalizationId, itemDetails.cartItemId)}
                                >
                                    Edit
                                </Button>
                            </>
                        )}

                        <div className={classes.reviewText}>
                            {description}
                        </div>
                    </Grid>

                )}
            {openModal && (
                <AddonVerification
                    selectedSku={selectedSku || {}}
                    productSkus={product?.productSkus}
                    isAddonAvailable
                    classes={classes}
                    open={openModal}
                    setOpen={setOpenModal}
                    pdpInlineAddon
                    handleAddToCart={handleAddToCart}
                    productBrand={product?.brand || null}
                    defaultSku={selectedSku?.id}
                />
            )}
        </>
    );
};

PmallSummary.propTypes = {
    classes: object.isRequired,
    product: object,
    itemDetails: shape({
        personalization: shape({
            personalizationId: string,
            personalizationType: string,
        }),
    }),
    // callLoadPmallSummary: func,
    onHandleModalPmall: func,
    pageType: string,
    pmallSummaryInfo: shape({
        customizations: shape({
            refnum: string,
        }),
    }).isRequired,
    currentBrandPresentation: string,
    selectedSku: shape({
        personalization: shape({
            personalizationAllowed: string,
        }),
    }),
    location: shape({
        state: shape({
            categoryId: string,
            categoryPath: string,
            categoryName: string,
        }),
    }),
    quantity: number,
    handleMiniCartModalClick: func,
};

PmallSummary.defaultProps = {
    product: {},
    itemDetails: {
        personalization: {
            personalizationId: '',
            personalizationType: '',
        },
    },
    onHandleModalPmall: () => {},
    pageType: 'cart',
    currentBrandPresentation: '',
    selectedSku: {},
    location: {},
    quantity: 1,
    handleMiniCartModalClick: () => {},
};

const mapStateToProps = (state) => ({
    pmallSummaryInfo: getPmallSummaryInfo(state),
    currentBrandPresentation: getPresentationFamily(state),
});

const enhance = compose(
    connect(mapStateToProps),
    withStyles(styles),
);

export default enhance(PmallSummary);
